import React from 'react';
import { cn } from '@rocco/utils/cn';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import type { FilterConfigBarItemColor } from '@rocco/ui/filter-sort/types/filter-config-bar';
import type { MultipleValuesFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/multiple-values-filter-state';
import type { FilterBarElementProps } from '@rocco/ui/filter-sort/components/filters-bar/FiltersBarElement';
import { getSafeSwatchBackground } from '@rocco/ui/filter-sort/utils/color-swatch';

/**
 * Color swatch filter for selecting colors from a grid.
 * Displays circular color swatches with selection ring and validates color values for security.
 *
 * @example
 * <FiltersBarColors filterConfigItem={colorConfig} filterState={colorState} onFilterStateUpdate={updateFilter} />
 */
export const FiltersBarColors = (
    props: FilterBarElementProps<
        FilterConfigBarItemColor,
        MultipleValuesFilterState
    >,
) => {
    const { filterState, onFilterStateUpdate } = props;
    const {
        searchFilters: { handleToggleMultipleValues },
    } = useFiltersContext();

    return (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {filterState.items.map((item) => {
                // Get validated background color
                // item.label can be:
                // - merged hex colors like "#fff,#aaa,#000" (shown as linear gradient)
                // - raw CSS value like "linear-gradient(180deg, #D8D8D8 0%, #B4B4B4 100%)"
                const background = getSafeSwatchBackground(item.label);

                return (
                    <button
                        key={item.value}
                        type="button"
                        aria-label={`Select color ${item.value}`}
                        aria-pressed={item.checked}
                        className={cn(
                            'flex cursor-pointer items-center gap-2 text-left transition-all',
                            'rounded focus:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-1',
                            item.checked ? 'font-medium' : '',
                        )}
                        data-color={item.value}
                        onClick={() => {
                            if (handleToggleMultipleValues) {
                                onFilterStateUpdate(
                                    handleToggleMultipleValues(
                                        filterState,
                                        item,
                                        !item.checked,
                                    ),
                                    true, // refetch = true to update URL
                                );
                            }
                        }}
                    >
                        <span
                            className={cn(
                                'size-5 shrink-0 rounded-full border-2',
                                item.checked
                                    ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-1'
                                    : 'border-gray-200',
                            )}
                            style={{
                                background,
                                backgroundOrigin: 'border-box',
                            }}
                        />
                        <span className="truncate text-3.5 capitalize">
                            {item.value}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};
